import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useApiFetch } from '../lib/api'
import { PencilIcon } from '../components/icons/PencilIcon'
import { HeartbeatIcon } from '../components/icons/HeartbeatIcon'
import { CopyIcon } from '../components/icons/CopyIcon'
import { McpNameWithBadge } from '../components/mcp/mcpShared'
import { McpConnectionsPage } from '../components/mcp/McpConnectionsPage'
import type { MambuConnectionCatalogItem, MambuConnectionDetail } from '../lib/types'

/* ─── MCP Config page ───
   Thin wrapper around McpConnectionsPage. Only the live-item actions (copy URL +
   configure) and the HeartbeatIcon avatar are specific to this surface; everything
   else (catalog state, preview cards, page skeleton) lives in McpConnectionsPage. */

/* Prototype: agent-studio.html:5469 (grid, 22) vs :5522 (list, 18). */
function PulseAvatar({ size }: { readonly size: 22 | 18 }) {
  return (
    <div className="agent-grid-icon" aria-hidden="true" style={{ color: 'var(--accent-text)' }}>
      <HeartbeatIcon size={size} />
    </div>
  )
}

interface McpCoreActionButtonsProps {
  readonly item: MambuConnectionCatalogItem
  readonly setToast: (msg: string | null) => void
  readonly copyServerUrl: string
  readonly copyServerUrlAria: string
  readonly configure: string
  readonly configureAria: string
  readonly onCopy: (item: MambuConnectionCatalogItem, setToast: (msg: string | null) => void) => Promise<void>
  readonly onConfigure: () => void
}

/* The copy+configure button group is identical between the grid card and list row.
   Extracted here to avoid token-level duplication between the two render functions. */
function McpCoreActionButtons({
  item, setToast, copyServerUrl, copyServerUrlAria, configure, configureAria, onCopy, onConfigure,
}: McpCoreActionButtonsProps) {
  return (
    <>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{copyServerUrl}</span>
      <button
        type="button"
        className="skill-edit-btn"
        onClick={() => void onCopy(item, setToast)}
        title={copyServerUrl}
        aria-label={copyServerUrlAria}
        style={{ color: 'var(--accent)' }}
      >
        <CopyIcon />
      </button>
      <button
        type="button"
        className="skill-edit-btn"
        /* Prototype (agent-studio.html:5477, :5530) gives this action the SHORT verb
           as its hover tooltip, while the icon-only button still needs a longer
           accessible name — so title and aria-label deliberately differ. */
        title={configure}
        aria-label={configureAria}
        onClick={onConfigure}
      >
        <PencilIcon />
      </button>
    </>
  )
}

export function MambuMcp() {
  const navigate = useNavigate()
  const apiFetch = useApiFetch()
  const { t } = useTranslation()

  async function handleCopy(item: MambuConnectionCatalogItem, setToast: (msg: string | null) => void) {
    if (!item.id) return
    try {
      const detail = await apiFetch<MambuConnectionDetail>(`/api/v1/mambu-connections/${item.id}`)
      await navigator.clipboard.writeText(detail.remoteEndpoint)
      setToast(t('mambuMcp.core.copiedToast'))
    } catch {
      /* silent — no error UI for this one-off copy action per plan */
    }
  }

  function renderLiveCard(item: MambuConnectionCatalogItem, setToast: (msg: string | null) => void) {
    return (
      <div // NOSONAR(typescript:S6819): the prototype makes the whole card clickable while it also holds its own action buttons — a <button> wrapper would nest buttons (invalid HTML), so role="button" + tabIndex + Enter handling is the accessible equivalent here.
        key={item.key}
        role="button"
        tabIndex={0}
        aria-label={t('mambuMcp.core.configureAria')}
        onClick={() => navigate('/mcp/core/tenant')}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); navigate('/mcp/core/tenant') } }}
        className="agent-grid-card mcpconn-clickable"
      >
        <div className="agent-grid-card-header">
          <PulseAvatar size={22} />
        </div>
        <McpNameWithBadge item={item} />
        {/* The MCP Config surface carries its own copy for the Core connection —
            "Live access to core banking data and operations" (agent-studio.html:5471,
            :5525, :5579) — not the connection's stored description, which the prototype
            renders on the Agent → Connections tab instead (:4047). Two surfaces, two
            strings, so this deliberately does not fall back to item.description. */}
        <div className="agent-grid-desc">{t('mambuMcp.core.description')}</div>
        <div className="agent-grid-footer">
          <div />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={(e) => e.stopPropagation()}>
            <McpCoreActionButtons
              item={item} setToast={setToast} onCopy={handleCopy}
              onConfigure={() => navigate('/mcp/core/tenant')}
              copyServerUrl={t('mambuMcp.core.copyServerUrl')}
              copyServerUrlAria={t('mambuMcp.core.copyServerUrlAria')}
              configure={t('mambuMcp.core.configure')}
              configureAria={t('mambuMcp.core.configureAria')}
            />
          </div>
        </div>
      </div>
    )
  }

  function renderLiveRow(item: MambuConnectionCatalogItem, setToast: (msg: string | null) => void) {
    return (
      <div // NOSONAR(typescript:S6819): same as the grid card above — clickable row with nested action buttons, so <button> is not usable.
        key={item.key}
        role="button"
        tabIndex={0}
        aria-label={t('mambuMcp.core.configureAria')}
        onClick={() => navigate('/mcp/core/tenant')}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); navigate('/mcp/core/tenant') } }}
        className="agent-list-row mcpconn-clickable"
      >
        <PulseAvatar size={18} />
        <div className="agent-list-info">
          <McpNameWithBadge item={item} />
          {/* Surface copy, not the stored description — see the grid card above. */}
          <div className="agent-grid-desc">{t('mambuMcp.core.description')}</div>
        </div>
        <div className="agent-list-actions" style={{ gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <McpCoreActionButtons
            item={item} setToast={setToast} onCopy={handleCopy}
            onConfigure={() => navigate('/mcp/core/tenant')}
            copyServerUrl={t('mambuMcp.core.copyServerUrl')}
            copyServerUrlAria={t('mambuMcp.core.copyServerUrlAria')}
            configure={t('mambuMcp.core.configure')}
            configureAria={t('mambuMcp.core.configureAria')}
          />
        </div>
      </div>
    )
  }

  return (
    <McpConnectionsPage
      heading={t('mambuMcp.heading')}
      subtitle={t('mambuMcp.subtitle')}
      failedToLoad={t('mambuMcp.failedToLoad')}
      gridViewLabel={t('mambuMcp.gridView')}
      listViewLabel={t('mambuMcp.listView')}
      cssPrefix="mcpconn"
      voteSurface="config"
      disabledActionIcon={<PencilIcon />}
      renderAvatar={(size) => <PulseAvatar size={size} />}
      renderLiveCard={renderLiveCard}
      renderLiveRow={renderLiveRow}
    />
  )
}
